import { JobSeriesType } from "@constants/choices";
import { PERMISSION_LEVEL } from "@constants/permissionLevel";
import { renderServiceLocationString } from "@legacy/clients/utils/utils";
import Spinner from "@legacy/core/components/Spinner";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import debounce from "debounce-promise";
import Cookies from "js-cookie";
import pluralize from "pluralize";
import { Component } from "react";
import deepcopy from "rfdc";
import ClientForm from "../clients/forms/ClientForm";
import ContactForm from "../clients/forms/ContactForm";
import ServiceLocationForm from "../clients/forms/ServiceLocationForm";
import { ClientTypes, CustomIDGenerationModes, LineItemUnitTypes, PriceBookItemTypes } from "../core/utils/enums";
import { currencyFormatter, fetchIntersectedLabels, getCurrencySymbol, historyHasState, sendDataToServer, valueIsDefined } from "../core/utils/utils";
import EquipmentForm from "../equipment/forms/EquipmentForm";
import PriceBookItemForm from "../pricebook/forms/PriceBookItemForm";
import { getPricebookTaxById } from "../pricebook/utils/utils";
import JobForm from "./forms/JobForm";

dayjs.extend(timezone)


const FORM_MODES = {
    ADD_JOB: "ADD_JOB",
    EDIT_JOB: "EDIT_JOB",
    ADD_CLIENT: "ADD_CLIENT",
    EDIT_CLIENT: "EDIT_CLIENT",
    ADD_BILLING_CLIENT: "ADD_BILLING_CLIENT",
    EDIT_BILLING_CLIENT: "EDIT_BILLING_CLIENT",
    ADD_SERVICE_LOCATION: "ADD_SERVICE_LOCATION",
    EDIT_SERVICE_LOCATION: "EDIT_SERVICE_LOCATION",
    ADD_PRICEBOOKITEM_SERVICE: "ADD_PRICEBOOKITEM_SERVICE",
    EDIT_PRICEBOOKITEM_SERVICE: "EDIT_PRICEBOOKITEM_SERVICE",
    ADD_PRICEBOOKITEM_TAX: "ADD_PRICEBOOKITEM_TAX",
    EDIT_PRICEBOOKITEM_TAX: "EDIT_PRICEBOOKITEM_TAX",
    ADD_EQUIPMENT: "ADD_EQUIPMENT",
    ADD_CONTACT: "ADD_CONTACT",
    EDIT_CONTACT: "EDIT_CONTACT",
}

const FORM_MODE_SUBTITLES = {
    ADD_JOB: "Add Job Details",
    EDIT_JOB: "Edit Job Details",
    ADD_CLIENT: "New Client",
    EDIT_CLIENT: "Edit Client",
    ADD_BILLING_CLIENT: "New Billing Client",
    EDIT_BILLING_CLIENT: "Edit Billing Client",
    ADD_SERVICE_LOCATION: "Add Service Location",
    EDIT_SERVICE_LOCATION: "Edit Service Location",
    ADD_PRICEBOOKITEM_SERVICE: "Add PriceBook Service",
    EDIT_PRICEBOOKITEM_SERVICE: "Edit PriceBook Service",
    ADD_PRICEBOOKITEM_TAX: "Add PriceBook Tax",
    EDIT_PRICEBOOKITEM_TAX: "Edit PriceBook Tax",
    ADD_EQUIPMENT: "Log New Equipment",
    ADD_CONTACT: "Add Contact",
    EDIT_CONTACT: "Edit Contact",
}

const FORM_MODE_BACK_BUTTON_DISPLAY = {
    ADD_JOB: "flex",
    EDIT_JOB: "flex",
    ADD_CLIENT: "none",
    EDIT_CLIENT: "none",
    ADD_BILLING_CLIENT: "none",
    EDIT_BILLING_CLIENT: "none",
    ADD_SERVICE_LOCATION: "none",
    EDIT_SERVICE_LOCATION: "none",
    ADD_PRICEBOOKITEM_SERVICE: "none",
    EDIT_PRICEBOOKITEM_SERVICE: "none",
    ADD_PRICEBOOKITEM_TAX: "none",
    EDIT_PRICEBOOKITEM_TAX: "none",
    ADD_EQUIPMENT: "none",
    ADD_CONTACT: "none",
    EDIT_CONTACT: "none",
}

const PRIMARY_FORM_MODES = [FORM_MODES.ADD_JOB, FORM_MODES.EDIT_JOB]
const SECONDARY_FORM_MODES = [
    FORM_MODES.ADD_CLIENT,
    FORM_MODES.EDIT_CLIENT,
    FORM_MODES.ADD_BILLING_CLIENT,
    FORM_MODES.EDIT_BILLING_CLIENT,
    FORM_MODES.ADD_SERVICE_LOCATION,
    FORM_MODES.EDIT_SERVICE_LOCATION,
    FORM_MODES.ADD_PRICEBOOKITEM_SERVICE,
    FORM_MODES.EDIT_PRICEBOOKITEM_SERVICE,
    FORM_MODES.ADD_PRICEBOOKITEM_TAX,
    FORM_MODES.EDIT_PRICEBOOKITEM_TAX,
    FORM_MODES.ADD_EQUIPMENT,
    FORM_MODES.ADD_CONTACT,
    FORM_MODES.EDIT_CONTACT,
]

const CLIENT_FORM_MODES = [
    FORM_MODES.ADD_CLIENT,
    FORM_MODES.EDIT_CLIENT,
    FORM_MODES.ADD_BILLING_CLIENT,
    FORM_MODES.EDIT_BILLING_CLIENT,
]

const SERVICE_LOCATION_FORM_MODES = [
    FORM_MODES.ADD_SERVICE_LOCATION,
    FORM_MODES.EDIT_SERVICE_LOCATION,
]

const PRICEBOOK_SERVICE_FORM_MODES = [
    FORM_MODES.ADD_PRICEBOOKITEM_SERVICE,
    FORM_MODES.EDIT_PRICEBOOKITEM_SERVICE,
    FORM_MODES.ADD_PRICEBOOKITEM_TAX,
    FORM_MODES.EDIT_PRICEBOOKITEM_TAX,
]

const EQUIPMENT_FORM_MODES = [
    FORM_MODES.ADD_EQUIPMENT,
]

const CONTACT_FORM_MODES = [
    FORM_MODES.ADD_CONTACT,
    FORM_MODES.EDIT_CONTACT,
]

const FORM_DATA_NAMES_BY_MODE = {
    ADD_JOB: "jobData",
    EDIT_JOB: "jobData",
    ADD_CLIENT: "clientData",
    EDIT_CLIENT: "clientData",
    ADD_BILLING_CLIENT: "billingClientData",
    EDIT_BILLING_CLIENT: "billingClientData",
    ADD_SERVICE_LOCATION: "serviceLocationData",
    EDIT_SERVICE_LOCATION: "serviceLocationData",
    ADD_PRICEBOOKITEM_SERVICE: "priceBookItemData",
    EDIT_PRICEBOOKITEM_SERVICE: "priceBookItemData",
    ADD_PRICEBOOKITEM_TAX: "priceBookItemData",
    EDIT_PRICEBOOKITEM_TAX: "priceBookItemData",
    ADD_EQUIPMENT: "equipmentData",
    ADD_CONTACT: "contactData",
    EDIT_CONTACT: "contactData",
}

const SUBMITTING_NAMES_BY_MODE = {
    ADD_JOB: "submittingJob",
    EDIT_JOB: "submittingJob",
    ADD_CLIENT: "submittingClient",
    EDIT_CLIENT: "submittingClient",
    ADD_BILLING_CLIENT: "submittingBillingClient",
    EDIT_BILLING_CLIENT: "submittingBillingClient",
    ADD_SERVICE_LOCATION: "submittingServiceLocation",
    EDIT_SERVICE_LOCATION: "submittingServiceLocation",
    ADD_PRICEBOOKITEM_SERVICE: "submittingPriceBookItem",
    EDIT_PRICEBOOKITEM_SERVICE: "submittingPriceBookItem",
    ADD_PRICEBOOKITEM_TAX: "submittingPriceBookItem",
    EDIT_PRICEBOOKITEM_TAX: "submittingPriceBookItem",
    ADD_EQUIPMENT: "submittingEquipment",
    ADD_CONTACT: "submittingContact",
    EDIT_CONTACT: "submittingContact",
}

const ERROR_NAMES_BY_MODE = {
    ADD_JOB: "job",
    EDIT_JOB: "job",
    ADD_CLIENT: "client",
    EDIT_CLIENT: "client",
    ADD_BILLING_CLIENT: "billingClient",
    EDIT_BILLING_CLIENT: "billingClient",
    ADD_SERVICE_LOCATION: "serviceLocation",
    EDIT_SERVICE_LOCATION: "serviceLocation",
    ADD_PRICEBOOKITEM_SERVICE: "priceBookItem",
    EDIT_PRICEBOOKITEM_SERVICE: "priceBookItem",
    ADD_PRICEBOOKITEM_TAX: "priceBookItem",
    EDIT_PRICEBOOKITEM_TAX: "priceBookItem",
    ADD_EQUIPMENT: "equipment",
    ADD_CONTACT: "contact",
    EDIT_CONTACT: "contact",
}


class JobCreateContainer extends Component {

    // Initialize

    constructor(props) {
        super(props)

        const defaultMode = this.props.formMode || FORM_MODES.ADD_JOB
        this.addToastToQueue = this.props.addToastToQueue
        this.backDestination = this.props.backDestination

        this.workingTechnicianOptions = window.WORKING_TECHNICIANS || []
        this.similarJobCache = {}
        this.duplicateEquipmentCache = {}
        this.outstandingBalanceCache = {}

        this.state = {
            jobData: null,
            estimateData: null,
            recallToData: null,
            clientData: {},
            billingClientData: {},
            serviceLocationData: {},
            priceBookItemData: {},
            equipmentData: {},
            contactData: {},

            similarJobData: [],
            outstandingBalance: null,
            duplicateEquipmentData: [],

            pendingSessionId: crypto.randomUUID(),
            attachments: [],
            isUploading: false,

            selectedClient: null,
            selectedBillingClient: null,
            selectedServiceLocation: null,
            selectedPriceBookService: null,
            selectedEquipment: [],
            selectedReporter: null,
            selectedPointOfContact: null,

            isDraft: false,

            errors: {
                job: {},
                client: {},
                billingClient: {},
                serviceLocation: {},
                priceBookItem: {},
                equipment: {},
                contact: {},
            },

            defaultMode: defaultMode,
            mode: defaultMode,

            priceBookServices: window.PRICEBOOK_SERVICES || [],
            priceBookTaxes: window.PRICEBOOK_TAXES || [],
            equipmentCategoryOptions: window.EQUIPMENT_CATEGORIES || [],
            equipmentTypeOptions: window.EQUIPMENT_TYPES || [],
            ownershipTypeOptions: window.OWNERSHIP_TYPES || [],
            jobOriginTypeOptions: window.JOB_ORIGIN_TYPES || [],
            allJobOriginTypes: window.ALL_JOB_ORIGIN_TYPES || [],
            priorityLevelsOptions: window.PRIORITY_LEVELS || [],

            useTaxes: window.USE_TAXES,
            pricebook_default_taxable_service: window.PRICEBOOK_DEFAULT_TAXABLE_SERVICE,
            pricebook_default_taxable_part: window.PRICEBOOK_DEFAULT_TAXABLE_PART,
            pricebook_default_taxable_other: window.PRICEBOOK_DEFAULT_TAXABLE_OTHER,

            preferredTimezone: window.PREFERRED_TIMEZONE,
            phoneNumberCountry: window.PHONE_NUMBER_COUNTRY,
            defaultClientType: window.DEFAULT_CLIENT_TYPE,
            currencyCode: window.CURRENCY_CODE,
            languageCode: window.LANGUAGE_CODE,

            showJobOriginFields: window.SHOW_JOB_ORIGIN_FIELDS,
            showCustomJobIDField: window.JOB_CUSTOM_ID_GENERATION_MODE === CustomIDGenerationModes.manual,

            showQuickBooksRevenueAccountSelect: window.ACCOUNTING_INTEGRATION === 2 && window.QUICKBOOKS_LINE_ITEM_SCHEME === 2,
            showQuickBooksTaxAgencyVendorSelect: window.ACCOUNTING_INTEGRATION === 2,
            showTaxCreateButton: window.ACCOUNTING_INTEGRATION === 0,

            returnScroll: 0,

            showEquipmentWipedWarning: false,
            showContactsWipedWarning: false,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    componentDidMount = async () => {
        const searchParams = new URLSearchParams(document.location.search)
        const startDate = searchParams.get("start_date")
        const endDate = searchParams.get("end_date")
        const resourceId = searchParams.get("technician_id")

        if (this.state.jobData === null) {
            if (this.state.defaultMode === FORM_MODES.EDIT_JOB && window.JOB_ID) {
                const endpoint = DjangoUrls["jobs:api-jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, window.JOB_ID)
                const response = await fetch(endpoint)

                let job

                if (response.ok) {
                    job = await response.json()

                    this.setState((state, props) => {
                        let updatedState = state

                        updatedState.selectedClient = job.service_location.external_client
                        updatedState.selectedServiceLocation = job.service_location
                        updatedState.selectedEquipment = job.equipment
                        updatedState.selectedPriceBookService = state.priceBookServices.find(service => service.description === job.service_name) || null

                        // Because contacts are based on the service location, we can't just pull a full list and find a match.
                        if ([job.reporter_name, job.reporter_phone, job.reporter_email].some(detail => detail !== "")) {
                            updatedState.selectedReporter = {
                                "composite_key": `${job.reporter_name}_${job.reporter_phone}_${job.reporter_phone_extension}_${job.reporter_email}`,
                                "name": job.reporter_name,
                                "phone": job.reporter_phone,
                                "phone_extension": job.reporter_phone_extension,
                                "email": job.reporter_email,
                                "is_ephemeral": job.reporter_is_ephemeral,
                            }
                        }
                        if ([job.point_of_contact_name, job.point_of_contact_phone, job.point_of_contact_email].some(detail => detail !== "")) {
                            updatedState.selectedPointOfContact = {
                                "composite_key": `${job.point_of_contact_name}_${job.point_of_contact_phone}_${job.point_of_contact_phone_extension}_${job.point_of_contact_email}`,
                                "name": job.point_of_contact_name,
                                "phone": job.point_of_contact_phone,
                                "phone_extension": job.point_of_contact_phone_extension,
                                "email": job.point_of_contact_email,
                                "is_ephemeral": job.point_of_contact_is_ephemeral,
                            }
                        }

                        updatedState.jobData = job
                        updatedState.isDraft = job.is_draft

                        updatedState.attachments = deepcopy()(job.attachments)
                        updatedState.labels = deepcopy()(job.labels)

                        // Convert service location to ID
                        updatedState.jobData.service_location = job.service_location.id

                        // Convert assigned technician objects to IDs and filter working technicians
                        const assignedTechnicianIDs = job.assigned_technicians.map(
                            technician => technician.id
                        ).filter(technicianId =>
                            this.workingTechnicianOptions.some(option => option.value === technicianId)
                        )
                        updatedState.jobData.assigned_technicians = assignedTechnicianIDs

                        // Convert equipment to IDs
                        const equipmentIDs = job.equipment.map(equipment => equipment.id)
                        updatedState.jobData.equipment = equipmentIDs

                        // Convert attachments to ids
                        updatedState.jobData.estimate = job.estimate ? job.estimate.id : null
                        updatedState.jobData.recall_to = job.recall_to ? job.recall_to.id : null
                        updatedState.jobData.series_origin = job.series_origin ? job.series_origin.id : null

                        // Convert billing_client to ID (if exists)
                        if (job.billing_client) {
                            updatedState.selectedBillingClient = job.billing_client
                            updatedState.jobData.billing_client = job.billing_client.id
                        }

                        this.resetJobData(updatedState, state, job)

                        return updatedState
                    })
                }
            }
            else if (this.state.defaultMode === FORM_MODES.ADD_JOB) {
                const localTime = dayjs.tz(undefined, window.PREFERRED_TIMEZONE)
                const localDateString = localTime.format("YYYY-MM-DD")

                this.setState((state, props) => {
                    let updatedState = state
                    this.resetJobData(updatedState, state, {})
                    updatedState.jobData.date_received = localDateString
                    updatedState.jobData.attachments = []
                    updatedState.jobData.billing_intention = null
                    updatedState.jobData.billing_client = null

                    if (window.CURRENT_USER?.service_company?.default_job_technician_instructions) {
                        updatedState.jobData.technician_instructions = window.CURRENT_USER.service_company.default_job_technician_instructions
                    }
                    if (window.CURRENT_USER?.service_company?.default_job_equipment_expected) {
                        updatedState.jobData.equipment_expected = window.CURRENT_USER.service_company.default_job_equipment_expected
                    }

                    if (startDate !== null) {
                        updatedState.jobData.start_time = new Date(startDate)
                    }
                    if (startDate !== null && endDate !== null) {
                        const estimatedDurationInSeconds = dayjs(endDate).diff(dayjs(startDate))
                        const estimatedDuration = estimatedDurationInSeconds / 1000

                        updatedState.jobData.duration = estimatedDuration
                    }
                    if (resourceId !== null) {
                        updatedState.jobData.assigned_technicians = [parseInt(resourceId)]
                    }

                    updatedState.jobData.recurrence_enabled = false
                    updatedState.jobData.series_type = JobSeriesType.UPFRONT
                    updatedState.jobData.recurrence_interval = null
                    updatedState.jobData.recurrence_end = null
                    updatedState.jobData.recurrence_frequency = null
                    updatedState.jobData.weekly_recurrence = null
                    updatedState.jobData.monthly_recurrence = null
                    updatedState.jobData.labels = []


                    return updatedState
                })
            }
        }

        const hasEstimatesEntitlement = !!window.CURRENT_USER?.service_company?.entitlement_estimates_enabled;
        const hasEstimatesPermissions = window.CURRENT_USER?.permissions.estimates_view_permission >= PERMISSION_LEVEL.FULL
        const fromEstimateID = new URLSearchParams(document.location.search).get("from_estimate") || null
        if (hasEstimatesEntitlement && hasEstimatesPermissions && fromEstimateID !== null) {
            if (this.state.estimateData === null) {
                let estimateEndpoint = DjangoUrls["estimates:api-estimates-detail"](window.MARKETPLACE_ENTITY_SLUG, fromEstimateID)

                const estimateResponse = await fetch(estimateEndpoint)
                const estimate = await estimateResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.estimateData = estimate

                    // Strip IDs from all line items first to prevent line item stealing during conversion
                    const lineItemsWithoutIds = estimate.line_items.map(({ id, ...lineItem }) => lineItem);

                    // Split line items into their respective lists
                    updatedState.estimateData.service_charges = lineItemsWithoutIds.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.service)
                    updatedState.estimateData.parts = lineItemsWithoutIds.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                    updatedState.estimateData.other_charges = lineItemsWithoutIds.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.other)
                    updatedState.estimateData.discounts = lineItemsWithoutIds.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.discount)

                    updatedState.jobData.estimate = estimate.id
                    updatedState.jobData.external_client = estimate.service_location.external_client.id
                    updatedState.jobData.service_location = estimate.service_location.id
                    updatedState.jobData.details = estimate.details
                    updatedState.jobData.equipment_expected = estimate.equipment.length > 0
                    updatedState.jobData.equipment = estimate.equipment.map((equipment)=>equipment.id)


                    updatedState.selectedClient = estimate.service_location.external_client
                    updatedState.selectedServiceLocation = estimate.service_location
                    updatedState.selectedEquipment = estimate.equipment

                    updatedState.jobData.service_name = updatedState.estimateData.service_name
                    updatedState.selectedPriceBookService = state.priceBookServices.find(service => service.description === updatedState.estimateData.service_name) || null
                    updatedState.jobData.duration = updatedState.selectedPriceBookService?.default_job_duration

                    updatedState.jobData.line_items = [...updatedState.estimateData.service_charges, ...updatedState.estimateData.parts, ...updatedState.estimateData.other_charges, ...updatedState.estimateData.discounts]
                    updatedState.jobData.tax_name = updatedState.estimateData.tax_name
                    updatedState.jobData.tax_percent = updatedState.estimateData.tax_percent
                    updatedState.jobData.tax_breakdown = updatedState.estimateData.tax_breakdown
                    updatedState.jobData.tax_quickbooks_desktop_item_id = updatedState.estimateData.tax_quickbooks_desktop_item_id

                    // Copy billing fields from estimate
                    updatedState.jobData.billing_intention = estimate.billing_intention
                    if (estimate.billing_client) {
                        updatedState.selectedBillingClient = estimate.billing_client
                        updatedState.jobData.billing_client = estimate.billing_client.id
                    }

                    return updatedState
                })

                // Bring over labels from the estimate that also exist on jobs
                if (estimate.labels?.length > 0) {
                    const jobLabelsEndpoint = DjangoUrls["jobs:api-jobs-labels-list"](window.MARKETPLACE_ENTITY_SLUG)
                    const intersectedLabels = await fetchIntersectedLabels(estimate.labels, jobLabelsEndpoint)
                    this.setState((state) => {
                        let updatedState = state
                        updatedState.jobData.labels = deepcopy()(intersectedLabels)
                        return updatedState
                    })
                }
            }
        }
        else {
            this.setState((state, props) => {
                let updatedState = state

                if (state.jobData.estimate !== null && state.jobData.estimate !== undefined) {
                    updatedState.estimateData = deepcopy()(state.jobData.estimate)
                    updatedState.jobData.estimate = state.jobData.estimate.id
                }
                else {
                    updatedState.estimateData = {}
                }

                updatedState.line_items = []
                return updatedState
            })
        }

        const recallToID = new URLSearchParams(document.location.search).get("recall_to") || null
        if (recallToID != null) {
            if (this.state.recallToData === null) {
                let recallToEndpoint = DjangoUrls["jobs:api-jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, recallToID)

                const recallToResponse = await fetch(recallToEndpoint)
                const recallTo = await recallToResponse.json()

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.recallToData = recallTo

                    updatedState.jobData.is_recall = true
                    updatedState.jobData.recall_to = recallTo.id
                    updatedState.jobData.external_client = recallTo.service_location.external_client.id
                    updatedState.jobData.service_location = recallTo.service_location.id

                    updatedState.selectedClient = recallTo.service_location.external_client
                    updatedState.selectedServiceLocation = recallTo.service_location
                    updatedState.selectedEquipment = recallTo.equipment

                    // Because contacts are based on the service location, we can't just pull a full list and find a match.
                    if ([recallTo.reporter_name, recallTo.reporter_phone, recallTo.reporter_email].some(detail => detail !== "")) {
                        updatedState.selectedReporter = {
                            "composite_key": `${recallTo.reporter_name}_${recallTo.reporter_phone}_${recallTo.reporter_phone_extension}_${recallTo.reporter_email}`,
                            "name": recallTo.reporter_name,
                            "phone": recallTo.reporter_phone,
                            "phone_extension": recallTo.reporter_phone_extension,
                            "email": recallTo.reporter_email,
                            "is_ephemeral": recallTo.reporter_is_ephemeral,
                        }
                    }
                    if ([recallTo.point_of_contact_name, recallTo.point_of_contact_phone, recallTo.point_of_contact_email].some(detail => detail !== "")) {
                        updatedState.selectedPointOfContact = {
                            "composite_key": `${recallTo.point_of_contact_name}_${recallTo.point_of_contact_phone}_${recallTo.point_of_contact_phone_extension}_${recallTo.point_of_contact_email}`,
                            "name": recallTo.point_of_contact_name,
                            "phone": recallTo.point_of_contact_phone,
                            "phone_extension": recallTo.point_of_contact_phone_extension,
                            "email": recallTo.point_of_contact_email,
                            "is_ephemeral": recallTo.point_of_contact_is_ephemeral,
                        }
                    }

                    updatedState.jobData.service_name = recallTo.service_name

                    updatedState.jobData.tax_name = recallTo.tax_name
                    updatedState.jobData.tax_percent = recallTo.tax_percent
                    updatedState.jobData.tax_breakdown = recallTo.tax_breakdown
                    updatedState.jobData.tax_quickbooks_desktop_item_id = recallTo.tax_quickbooks_desktop_item_id

                    updatedState.jobData.equipment_expected = recallTo.equipment_expected
                    updatedState.jobData.equipment = recallTo.equipment.map(equipment => equipment.id)
                    updatedState.jobData.origin_type = recallTo.origin_type
                    updatedState.jobData.origin_id = recallTo.origin_id

                    updatedState.jobData.details = recallTo.details
                    updatedState.jobData.labels = recallTo.labels

                    updatedState.jobData.reporter_name = recallTo.reporter_name
                    updatedState.jobData.reporter_phone = recallTo.reporter_phone
                    updatedState.jobData.reporter_phone_extension = recallTo.reporter_phone_extension
                    updatedState.jobData.reporter_email = recallTo.reporter_email
                    updatedState.jobData.reporter_is_ephemeral = recallTo.reporter_is_ephemeral

                    updatedState.jobData.point_of_contact_name = recallTo.point_of_contact_name
                    updatedState.jobData.point_of_contact_phone = recallTo.point_of_contact_phone
                    updatedState.jobData.point_of_contact_phone_extension = recallTo.point_of_contact_phone_extension
                    updatedState.jobData.point_of_contact_email = recallTo.point_of_contact_email
                    updatedState.jobData.point_of_contact_is_ephemeral = recallTo.point_of_contact_is_ephemeral

                    updatedState.jobData.technician_instructions = recallTo.technician_instructions

                    // Copy billing fields from original job
                    updatedState.jobData.billing_intention = recallTo.billing_intention
                    if (recallTo.billing_client) {
                        updatedState.selectedBillingClient = recallTo.billing_client
                        updatedState.jobData.billing_client = recallTo.billing_client.id
                    }

                    return updatedState
                })
            }
        }
        else {
            this.setState((state, props) => {
                let updatedState = state

                if (state.jobData.recall_to !== null && state.jobData.recall_to !== undefined) {
                    updatedState.recallToData = deepcopy()(state.jobData.recall_to)
                    updatedState.jobData.recall_to = state.jobData.recall_to.id
                }
                else {
                    updatedState.recallToData = {}
                }

                return updatedState
            })
        }

        if (historyHasState(history)) {
            document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[history.state.mode]
            const backButton = document.querySelector(".back-button");
            backButton && (backButton.style.display = FORM_MODE_BACK_BUTTON_DISPLAY[history.state.mode]);
            this.setState(history.state)
        }

        this.fetchDuplicateEquipment()
    }

    // Form helpers

    updateFormData = (formName, fieldName, fieldValue) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        }, this.fetchDuplicateEquipment)
    }

    switchFormMode = (mode) => {
        document.querySelector(".page-subtitle").innerHTML = FORM_MODE_SUBTITLES[mode]
        document.querySelector(".back-button").style.display = FORM_MODE_BACK_BUTTON_DISPLAY[mode]

        if (SECONDARY_FORM_MODES.includes(mode)) {
            history.replaceState(this.state, "", "")
        }

        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            history.pushState(updatedState, "", "?mode=" + mode.toLowerCase().replace(/_/g, "-"));
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.setState((state, props) => {
            let updatedState = state

            // Clear the secondary form data
            updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]] = {}
            updatedState[SUBMITTING_NAMES_BY_MODE[state.mode]] = false
            updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = {}

            return updatedState
        }, this.fetchDuplicateEquipment)
        this.switchFormMode(this.state.defaultMode)
    }

    switchToSecondaryForm = (newFormMode, data, initialData) => {
        this.setState((state, props) => {
            let updatedState = state
            // Set the scroll state
            updatedState.returnScroll = document.querySelector(".main").scrollTop

            updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = {}

            if (data !== null) {
                updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = deepcopy()(data)

                if (newFormMode === FORM_MODES.EDIT_PRICEBOOKITEM_TAX) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].returnMode = this.state.mode
                }
                else if (CLIENT_FORM_MODES.includes(newFormMode)) {
                    // Normalize the nested default_billing_client object (as returned by the API)
                    // into the split shape the form expects: an id on the form field and the full
                    // object on selectedDefaultBillingClient for display.
                    const defaultBillingClient = updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].default_billing_client
                    if (defaultBillingClient && typeof defaultBillingClient === "object") {
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].selectedDefaultBillingClient = defaultBillingClient
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].default_billing_client = defaultBillingClient.id
                    }
                }
            }
            else {
                if (newFormMode === FORM_MODES.ADD_CLIENT) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].client_type = window.DEFAULT_CLIENT_TYPE || ClientTypes.business
                }
                else if (newFormMode === FORM_MODES.ADD_BILLING_CLIENT) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].client_type = window.DEFAULT_CLIENT_TYPE || ClientTypes.business
                }
                else if (SERVICE_LOCATION_FORM_MODES.includes(newFormMode)) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].external_client = state.jobData.external_client  // Set the client id on the new service location
                }
                else if (newFormMode === FORM_MODES.ADD_PRICEBOOKITEM_SERVICE) {
                    // Set the type to `service` on the new PriceBook Item. Also set typical defaults
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].pricebook_item_type = PriceBookItemTypes.service
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].default_unit_type = LineItemUnitTypes.hourly
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].is_active = true
                }
                else if (newFormMode === FORM_MODES.ADD_PRICEBOOKITEM_TAX) {
                    // Set the type to `tax` on the new PriceBook Item.
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].pricebook_item_type = PriceBookItemTypes.tax
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].is_active = true
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].returnMode = this.state.mode
                }
                else if (EQUIPMENT_FORM_MODES.includes(newFormMode)) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].service_location = state.jobData.service_location  // Set the service location id on the new equipment

                    // Auto-set if there's only one option; the associated field is disabled if there's only one as well
                    const valueOptions = state.equipmentCategoryOptions.filter(option => option.value !== "")
                    if (valueOptions.length == 1) {
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].equipment_category = valueOptions[0].value
                    }
                    // Apply default ownership type if provided by service company defaults
                    if (valueIsDefined(window.CURRENT_USER?.service_company?.default_equipment_ownership_type)) {
                        updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].ownership_type = window.CURRENT_USER.service_company.default_equipment_ownership_type
                    }
                }
                else if (newFormMode === FORM_MODES.ADD_CONTACT) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].attached_to = "service_location"
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].is_ephemeral = false
                }
            }

            if (initialData !== null) {
                Object.assign(updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]], initialData)
            }

            return updatedState
        })

        this.switchFormMode(newFormMode)
    }

    resetJobData = (updatedState, state, jobData) => {
        updatedState.jobData = jobData
        updatedState.jobData.is_draft = state.isDraft

        if (this.workingTechnicianOptions.length === 1) {
            updatedState.jobData.assigned_technicians = [this.workingTechnicianOptions[0].value]
        }
    }

    updateClientSelection = (selectedClient) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedClient !== null) {
                // If a different client is selected, wipe the selected location and equipment
                if (state.jobData.external_client !== undefined && selectedClient.id !== state.jobData.external_client) {
                    updatedState.selectedServiceLocation = null
                    updatedState.showEquipmentWipedWarning = state.selectedEquipment.length !== 0
                    updatedState.selectedEquipment = []
                    updatedState.showContactsWipedWarning = state.selectedReporter !== null || state.selectedPointOfContact !== null
                    updatedState.selectedReporter = null
                    updatedState.selectedPointOfContact = null

                    updatedState.jobData.service_location = null
                    updatedState.jobData.equipment = []

                    updatedState.jobData.reporter = null
                    updatedState.jobData.reporter_name = ""
                    updatedState.jobData.reporter_phone = ""
                    updatedState.jobData.reporter_phone_extension = ""
                    updatedState.jobData.reporter_email = ""

                    updatedState.jobData.point_of_contact = null
                    updatedState.jobData.point_of_contact_name = ""
                    updatedState.jobData.point_of_contact_phone = ""
                    updatedState.jobData.point_of_contact_phone_extension = ""
                    updatedState.jobData.point_of_contact_email = ""

                    // Clear billing intention when client changes
                    updatedState.jobData.billing_intention = null
                    updatedState.jobData.billing_client = null
                    updatedState.selectedBillingClient = null
                }

                // Set the selection, set the data
                updatedState.selectedClient = selectedClient
                updatedState.jobData.external_client = selectedClient.id

                // Auto-select single location and apply billing defaults
                if (selectedClient.service_locations.length === 1) {
                    const serviceLocation = selectedClient.service_locations[0]
                    updatedState.selectedServiceLocation = serviceLocation
                    updatedState.jobData.service_location = serviceLocation.id
                    this.applyBillingDefaults(updatedState, serviceLocation, selectedClient)
                }
            }
            else {
                // Client was unset. Unset data
                updatedState.selectedClient = null
                updatedState.selectedServiceLocation = null
                updatedState.showEquipmentWipedWarning = state.selectedEquipment.length !== 0
                updatedState.selectedEquipment = []
                updatedState.showContactsWipedWarning = state.selectedReporter !== null || state.selectedPointOfContact !== null
                updatedState.selectedReporter = null
                updatedState.selectedPointOfContact = null

                // Clear billing intention when client is cleared
                updatedState.jobData.billing_intention = null
                updatedState.jobData.billing_client = null
                updatedState.selectedBillingClient = null
            }

            return updatedState
        }, this.fetchOutstandingBalance)
    }

    // Apply billing defaults based on service location or client
    // Service location takes precedence over client defaults
    applyBillingDefaults = (updatedState, serviceLocation, client) => {
        const serviceLocationBillingIntention = serviceLocation?.default_billing_intention
        const clientBillingIntention = client?.default_billing_intention

        const defaultBillingIntention = serviceLocationBillingIntention ?? clientBillingIntention
        const defaultBillingClient = serviceLocationBillingIntention != null
            ? serviceLocation.default_billing_client
            : client?.default_billing_client

        if (defaultBillingIntention != null) {
            updatedState.jobData.billing_intention = defaultBillingIntention
            if (defaultBillingClient) {
                updatedState.selectedBillingClient = defaultBillingClient
                updatedState.jobData.billing_client = defaultBillingClient.id
            }
        }
    }

    updateServiceLocationSelection = (selectedServiceLocation) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedServiceLocation !== null) {
                // If a different location is selected, wipe the selected equipment
                if (state.jobData.service_location !== undefined && selectedServiceLocation.id !== state.jobData.service_location) {
                    updatedState.showEquipmentWipedWarning = state.selectedEquipment.length !== 0
                    updatedState.selectedEquipment = []
                    updatedState.showContactsWipedWarning = state.selectedReporter !== null || state.selectedPointOfContact !== null
                    updatedState.selectedReporter = null
                    updatedState.selectedPointOfContact = null

                    updatedState.jobData.equipment = []

                    updatedState.jobData.reporter = null
                    updatedState.jobData.reporter_name = ""
                    updatedState.jobData.reporter_phone = ""
                    updatedState.jobData.reporter_phone_extension = ""
                    updatedState.jobData.reporter_email = ""

                    updatedState.jobData.point_of_contact = null
                    updatedState.jobData.point_of_contact_name = ""
                    updatedState.jobData.point_of_contact_phone = ""
                    updatedState.jobData.point_of_contact_phone_extension = ""
                    updatedState.jobData.point_of_contact_email = ""

                    // Clear billing intention when service location changes
                    updatedState.jobData.billing_intention = null
                    updatedState.jobData.billing_client = null
                    updatedState.selectedBillingClient = null
                }

                // Set the selection, set the data
                updatedState.selectedServiceLocation = selectedServiceLocation
                updatedState.jobData.service_location = selectedServiceLocation.id

                this.applyBillingDefaults(updatedState, selectedServiceLocation, updatedState.selectedClient)
            }
            else {
                // Service location was unset. Unset data
                updatedState.selectedServiceLocation = null
                updatedState.showEquipmentWipedWarning = state.selectedEquipment.length !== 0
                updatedState.selectedEquipment = []
                updatedState.showContactsWipedWarning = state.selectedReporter !== null || state.selectedPointOfContact !== null
                updatedState.selectedReporter = null
                updatedState.selectedPointOfContact = null

                // Clear billing intention when service location is cleared
                updatedState.jobData.billing_intention = null
                updatedState.jobData.billing_client = null
                updatedState.selectedBillingClient = null
            }

            return updatedState
        }, this.fetchSimilarJobs)
    }

    updatePriceBookServiceSelection = (selectedPriceBookService) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedPriceBookService !== null) {
                if (state.jobData.service_name !== selectedPriceBookService.description) {
                    updatedState.jobData.duration = selectedPriceBookService.default_job_duration
                }
                else if (updatedState.jobData.duration === undefined || updatedState.jobData.duration === "") {
                    updatedState.jobData.duration = selectedPriceBookService.default_job_duration
                }

                updatedState.selectedPriceBookService = selectedPriceBookService

                updatedState.jobData.service_name = selectedPriceBookService.description
            }
            else {
                updatedState.jobData.duration = ""

                updatedState.selectedPriceBookService = null

                updatedState.jobData.service_name = ""
            }

            return updatedState
        }, this.fetchSimilarJobs)
    }

    updateBillingClientSelection = (selectedBillingClient) => {
        this.setState((state) => {
            let updatedState = state

            if (selectedBillingClient !== null) {
                updatedState.selectedBillingClient = selectedBillingClient
                updatedState.jobData.billing_client = selectedBillingClient.id
            }
            else {
                updatedState.selectedBillingClient = null
                updatedState.jobData.billing_client = null
            }

            return updatedState
        })
    }

    updateDefaultBillingClientSelection = (selectedDefaultBillingClient) => {
        this.setState((state) => {
            let updatedState = state
            const formDataName = FORM_DATA_NAMES_BY_MODE[state.mode]

            if (selectedDefaultBillingClient !== null) {
                updatedState[formDataName].selectedDefaultBillingClient = selectedDefaultBillingClient
                updatedState[formDataName].default_billing_client = selectedDefaultBillingClient.id
            }
            else {
                updatedState[formDataName].selectedDefaultBillingClient = null
                updatedState[formDataName].default_billing_client = null
            }

            return updatedState
        })
    }

    updatePriceBookTaxSelection = (selectedPriceBookTax, then=null) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedPriceBookTax) {
                // Update the pricebook tax list with any changes
                if (getPricebookTaxById(selectedPriceBookTax.id, state.priceBookTaxes)) {
                    const updateIndex = state.priceBookTaxes.findIndex(tax => tax.id === selectedPriceBookTax.id)
                    updatedState.priceBookTaxes[updateIndex] = selectedPriceBookTax
                }
                else {
                    updatedState.priceBookTaxes.push(selectedPriceBookTax)
                }
            }

            // If no return mode is set (i.e. we're not on the pricebook item form, use the current mode)
            const parentMode = state[FORM_DATA_NAMES_BY_MODE[state.mode]].returnMode || state.mode

            if (!PRIMARY_FORM_MODES.includes(parentMode)) {
                updatedState[FORM_DATA_NAMES_BY_MODE[parentMode]].selectedPriceBookTax = selectedPriceBookTax
                updatedState[FORM_DATA_NAMES_BY_MODE[parentMode]].default_pricebook_tax = selectedPriceBookTax?.id || null
            }

            return updatedState
        }, then)
    }

    updateAttachments = (attachmentUploadData) => {
        this.setState((state, props) => {
            let updatedState = state

            updatedState.attachments.push(...attachmentUploadData)
            updatedState.jobData.attachments.push(...attachmentUploadData)

            return updatedState
        })
    }

    removePendingAttachment = (filenameOrId) => {
        this.setState((state, props) => {
            let updatedState = state

            // Remove pending attachments by filename OR existing attachments by ID
            const filterFn = (a) => {
                if (a.isPending && a.upload_filename === filenameOrId) return false
                if (a.id && String(a.id) === String(filenameOrId)) return false
                return true
            }

            updatedState.attachments = updatedState.attachments.filter(filterFn)
            updatedState.jobData.attachments = updatedState.jobData.attachments.filter(filterFn)

            return updatedState
        })
    }

    setIsUploading = (isUploading) => {
        this.setState({ isUploading })
    }

    updateEquipmentSelection = (selectedEquipment) => {
        this.setState((state, props) => {
            let updatedState = state

            updatedState.selectedEquipment = selectedEquipment
            updatedState.jobData.equipment = selectedEquipment.map(equipment => equipment.id)

            return updatedState
        })
    }

    updateReporterSelection = (selectedContact) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedContact !== null) {
                updatedState.selectedReporter = selectedContact

                updatedState.jobData.reporter_name = selectedContact.name
                updatedState.jobData.reporter_phone = selectedContact.phone
                updatedState.jobData.reporter_phone_extension = selectedContact.phone_extension
                updatedState.jobData.reporter_email = selectedContact.email
                updatedState.jobData.reporter_is_ephemeral = selectedContact.is_ephemeral
            }
            else {
                updatedState.selectedReporter = null

                updatedState.jobData.reporter_name = ""
                updatedState.jobData.reporter_phone = ""
                updatedState.jobData.reporter_phone_extension = ""
                updatedState.jobData.reporter_email = ""
                updatedState.jobData.reporter_is_ephemeral = false
            }

            return updatedState
        })
    }

    updatePointOfContactSelection = (selectedContact) => {
        this.setState((state, props) => {
            let updatedState = state

            if (selectedContact !== null) {
                updatedState.selectedPointOfContact = selectedContact

                updatedState.jobData.point_of_contact_name = selectedContact.name
                updatedState.jobData.point_of_contact_phone = selectedContact.phone
                updatedState.jobData.point_of_contact_phone_extension = selectedContact.phone_extension
                updatedState.jobData.point_of_contact_email = selectedContact.email
                updatedState.jobData.point_of_contact_is_ephemeral = selectedContact.is_ephemeral
            }
            else {
                updatedState.selectedPointOfContact = null

                updatedState.jobData.point_of_contact_name = ""
                updatedState.jobData.point_of_contact_phone = ""
                updatedState.jobData.point_of_contact_phone_extension = ""
                updatedState.jobData.point_of_contact_email = ""
                updatedState.jobData.point_of_contact_is_ephemeral = false
            }

            return updatedState
        })
    }

    fetchSimilarJobs = async () => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.similarJobData = []
            return updatedState
        })

        if (this.state.selectedServiceLocation !== null && this.state.selectedPriceBookService !== null && this.state.mode === FORM_MODES.ADD_JOB && window.CURRENT_USER?.permissions.jobs_list_permission >= PERMISSION_LEVEL.RESTRICTED) {
            const params = new URLSearchParams({
                "service_location": this.state.selectedServiceLocation.id,
                "service_name": this.state.selectedPriceBookService.description,
            })
            const endpoint = DjangoUrls["jobs:api-jobs-similar-list"](window.MARKETPLACE_ENTITY_SLUG) + `?${params.toString()}`
            let similarJobs

            if (params.toString() in this.similarJobCache) {
                similarJobs = this.similarJobCache[params.toString()]
            }
            else {
                const similarJobResponse = await fetch(endpoint)
                similarJobs = await similarJobResponse.json()
                this.similarJobCache[params.toString()] = similarJobs
            }

            this.setState((state, props) => {
                let updatedState = state
                updatedState.similarJobData = similarJobs
                return updatedState
            })
        }
    }

    fetchDuplicateEquipment = debounce(
        async () => {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.duplicateEquipmentData = []
                return updatedState
            })

            if (valueIsDefined(this.state.equipmentData.manufacturer) && valueIsDefined(this.state.equipmentData.model_number) && valueIsDefined(this.state.equipmentData.serial_number)) {
                const params = new URLSearchParams({
                    "manufacturer": this.state.equipmentData.manufacturer,
                    "model_number": this.state.equipmentData.model_number,
                    "serial_number": this.state.equipmentData.serial_number,
                })

                const endpoint = DjangoUrls["equipment:api-equipment-duplicates-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id) + `?${params.toString()}`
                let duplicateEquipment

                if (params.toString() in this.duplicateEquipmentCache) {
                    duplicateEquipment = this.duplicateEquipmentCache[params.toString()]
                }
                else {
                    const duplicateEquipmentResponse = await fetch(endpoint)
                    duplicateEquipment = await duplicateEquipmentResponse.json()
                    this.duplicateEquipmentCache[params.toString()] = duplicateEquipment
                }

                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.duplicateEquipmentData = duplicateEquipment.filter(equipment => equipment.id !== this.state.equipmentData.id)
                    return updatedState
                })
            }
        }, 500
    )

    fetchOutstandingBalance = async () => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.outstandingBalance = null
            return updatedState
        })

        if (this.state.selectedClient !== null) {
            const clientID = this.state.selectedClient.id
            const endpoint = DjangoUrls["clients:api-clients-balance"](window.MARKETPLACE_ENTITY_SLUG, clientID)
            let outstandingBalance

            if (clientID in this.outstandingBalanceCache) {
                outstandingBalance = this.outstandingBalanceCache[clientID]
            }
            else {
                const outstandingBalanceResponse = await fetch(endpoint)
                const outstandingBalanceData = await outstandingBalanceResponse.json()
                outstandingBalance = outstandingBalanceData.outstanding_balance
                this.outstandingBalanceCache[clientID] = outstandingBalance
            }

            this.setState((state, props) => {
                let updatedState = state
                updatedState.outstandingBalance = outstandingBalance
                return updatedState
            })
        }
    }

    // Crud job

    createJob = async () => {
        const endpoint = DjangoUrls["jobs:api-jobs-list"](window.MARKETPLACE_ENTITY_SLUG)
        let successUrl

        const onSuccess = async (job) => {
            // Create attachment records for pending uploads
            const pendingAttachments = this.state.attachments.filter(a => a.isPending)
            if (pendingAttachments.length > 0) {
                const attachmentsEndpoint = DjangoUrls["jobs:api-jobs-attachments-list"](window.MARKETPLACE_ENTITY_SLUG, job.id)

                let failedAttachmentCount = 0

                for (const attachment of pendingAttachments) {
                    try {
                        const response = await fetch(attachmentsEndpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                                'X-CSRFToken': Cookies.get("csrftoken"),
                            },
                            credentials: 'include',
                            body: JSON.stringify({
                                upload_filename: attachment.upload_filename,
                                upload_mimetype: attachment.upload_mimetype,
                                upload_size: attachment.upload_size,
                                file_path: attachment.filePath,
                                is_public: true,
                                version: 2,
                            }),
                        })
                        if (!response.ok) {
                            failedAttachmentCount++
                            console.error('Failed to create attachment record:', response.status, response.statusText)
                        }
                    } catch (error) {
                        failedAttachmentCount++
                        console.error('Failed to create attachment record:', error)
                    }
                }

                if (failedAttachmentCount > 0) {
                    this.addToastToQueue({
                        type: "error",
                        variant: "default",
                        title: `${failedAttachmentCount} ${pluralize("attachment", failedAttachmentCount)} failed to save`,
                    })
                }
            }

            if (window.CURRENT_USER?.permissions.jobs_list_permission < PERMISSION_LEVEL.FULL) {
                successUrl = DjangoUrls["dashboard:dashboard"](window.MARKETPLACE_ENTITY_SLUG)
            } else {
                successUrl = this.backDestination ?? DjangoUrls["jobs:jobs-list"](window.MARKETPLACE_ENTITY_SLUG)
            }

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Job ${job.recurrence_enabled ? "series" : `"${job.custom_id || job.id}"`} scheduled`,
                cta: (
                    !job.recurrence_enabled && window.CURRENT_USER?.permissions.jobs_view_permission >= PERMISSION_LEVEL.FULL ?
                    {
                        children: "View",
                        destination: DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, job.id)
                    }
                    :
                    undefined
                ),
                path: successUrl.split("?")[0],
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: `Job ${this.state.jobData.recurrence_enabled ? "series " : ""}could not be scheduled`,
            })
        }

        this.CUDJob(endpoint, "POST", onSuccess, onError)
    }

    createDraftJob = async (preview=false) => {
        const endpoint = DjangoUrls["jobs:api-jobs-create-as-draft"](window.MARKETPLACE_ENTITY_SLUG)
        let successUrl

        const onSuccess = async (job) => {
            // Create attachment records for pending uploads
            const pendingAttachments = this.state.attachments.filter(a => a.isPending)
            if (pendingAttachments.length > 0) {
                const attachmentsEndpoint = DjangoUrls["jobs:api-jobs-attachments-list"](window.MARKETPLACE_ENTITY_SLUG, job.id)

                let failedAttachmentCount = 0

                for (const attachment of pendingAttachments) {
                    try {
                        const response = await fetch(attachmentsEndpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json',
                                'X-CSRFToken': Cookies.get("csrftoken"),
                            },
                            credentials: 'include',
                            body: JSON.stringify({
                                upload_filename: attachment.upload_filename,
                                upload_mimetype: attachment.upload_mimetype,
                                upload_size: attachment.upload_size,
                                file_path: attachment.filePath,
                                is_public: true,
                                version: 2,
                            }),
                        })
                        if (!response.ok) {
                            failedAttachmentCount++
                            console.error('Failed to create attachment record:', response.status, response.statusText)
                        }
                    } catch (error) {
                        failedAttachmentCount++
                        console.error('Failed to create attachment record:', error)
                    }
                }

                if (failedAttachmentCount > 0) {
                    this.addToastToQueue({
                        type: "error",
                        variant: "default",
                        title: `${failedAttachmentCount} ${pluralize("attachment", failedAttachmentCount)} failed to save`,
                    })
                }
            }

            if (preview) {
                successUrl = DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, job.id)
            } else {
                if (window.CURRENT_USER?.permissions.jobs_list_permission < PERMISSION_LEVEL.FULL) {
                    successUrl = DjangoUrls["dashboard:dashboard"](window.MARKETPLACE_ENTITY_SLUG)
                } else {
                    successUrl = this.backDestination ?? DjangoUrls["jobs:jobs-list"](window.MARKETPLACE_ENTITY_SLUG)
                }
            }
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Job "${job.custom_id || job.id}" draft saved`,
                cta:
                    !preview && window.CURRENT_USER?.permissions.jobs_view_permission >= PERMISSION_LEVEL.FULL ?
                    {
                        children: "View",
                        destination: DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, job.id)
                    }
                    :
                    undefined,
                path: successUrl,
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Job draft could not be saved",
            })
        }

        this.CUDJob(endpoint, "POST", onSuccess, onError)
    }

    updateJob = async () => {
        const endpoint = DjangoUrls["jobs:api-jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, window.JOB_ID)
        const successUrl = DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, window.JOB_ID)
        const onSuccess = (job) => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Job "${job.custom_id || job.id}" updated`,
                path: successUrl.split("?")[0],
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Job could not be updated",
            })
        }

        this.CUDJob(endpoint, "PUT", onSuccess, onError)
    }

    deleteDraftJob = async () => {
        const endpoint = DjangoUrls["jobs:api-jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, window.JOB_ID)
        let successUrl = this.backDestination ?? DjangoUrls["jobs:jobs-list"](window.MARKETPLACE_ENTITY_SLUG)

        if (valueIsDefined(this.state.jobData.estimate)) {
            successUrl = DjangoUrls["estimates:estimates-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.jobData.estimate)
        }

        const onSuccess = () => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Job "${this.state.jobData.custom_id || this.state.jobData.id}" draft deleted`,
                path: successUrl.split("?")[0],
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        document.querySelectorAll("#message_modal_confirm_delete_draft .modal__close .button").forEach(button => button.style.display = "none")
        document.querySelector("#message_modal_confirm_delete_draft .modal__close .spinner-centered").style.display = "block"

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Job draft could not be deleted",
            })
            const deleteDraftModal = document.querySelector("#message_modal_confirm_delete_draft .modal__close")
            if (deleteDraftModal) {
                deleteDraftModal.innerHTML = '<span class="text-invalid"><strong>An unexpected error occurred.</strong></span>'
            }
        }

        this.CUDJob(endpoint, "DELETE", onSuccess, onError)
    }

    CUDJob = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "jobData"
        const submittingName = "submittingJob"
        const errorDictName = "job"

        const dataManipulator = (data, state) => {
            // Deepcopy first to avoid mutating state — without this,
            // destructive operations (like deleting attachments) corrupt
            // state and clear UI data when the server returns an error.
            const cleanData = deepcopy()(data)

            // Convert blank Job ID value to null
            cleanData.custom_id = cleanData.custom_id || null

            // Set a blank label list if there aren't any selected
            cleanData.labels = cleanData.labels || []

            // For existing objects with file uploads, attachments are managed by the
            // FileUpload component via the attachment API — don't send them in the
            // form payload or the nested serializer will overwrite them.
            // For new objects, filter out pending attachments — they're created after
            // the object exists.
            if (cleanData.id && window.FILE_UPLOADS_ENABLED) {
                delete cleanData.attachments
            } else if (cleanData.attachments) {
                cleanData.attachments = cleanData.attachments.filter(a => !a.isPending && a.id)
            }

            return cleanData
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, custom_id must make a unique set.") {
                errorDict["custom_id"] = "A job with this ID already exists."
            }
            else if (fieldName === "labels" && Array.isArray(message)) {
                errorDict["labels"] = "Labels must be fewer than 100 characters."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud Client

    createClient = async () => {
        const dataName = "clientData"
        const submittingName = "submittingClient"
        const errorDictName = "client"
        const endpoint = DjangoUrls["clients:api-clients-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (client) => {
            this.updateClientSelection(client)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Client "${client.name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Client could not be created",
            })
        }

        this.createUpdateClient(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    updateClient = async () => {
        const dataName = "clientData"
        const submittingName = "submittingClient"
        const errorDictName = "client"
        const endpoint = DjangoUrls["clients:api-clients-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id)
        const endpointMethod = "PUT"

        const onSuccess = (client) => {
            this.updateClientSelection(client)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Client "${client.name}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Client could not be updated",
            })
        }

        this.createUpdateClient(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    // Crud Billing Client

    createBillingClient = async () => {
        const dataName = "billingClientData"
        const submittingName = "submittingBillingClient"
        const errorDictName = "billingClient"
        const endpoint = DjangoUrls["clients:api-clients-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (client) => {
            this.updateBillingClientSelection(client)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                size: "md",
                title: `Billing client "${client.name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                size: "md",
                title: "Billing client could not be created",
            })
        }

        this.createUpdateClient(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    updateBillingClient = async () => {
        const dataName = "billingClientData"
        const submittingName = "submittingBillingClient"
        const errorDictName = "billingClient"
        const endpoint = DjangoUrls["clients:api-clients-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedBillingClient.id)
        const endpointMethod = "PUT"

        const onSuccess = (client) => {
            this.updateBillingClientSelection(client)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                size: "md",
                title: `Billing client "${client.name}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                size: "md",
                title: "Billing client could not be updated",
            })
        }

        this.createUpdateClient(endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName)
    }

    createUpdateClient = async (endpoint, endpointMethod, onSuccess, onError, dataName, submittingName, errorDictName) => {
        const dataManipulator = (data, state) => {
            data.industry_type = data.industry_type || null  // Convert blank Industry Type value to null
            data.default_invoice_net = data.default_invoice_net !== "" ? data.default_invoice_net : null  // Convert blank net value to null
            data.contacts = data.contacts || []

            return data
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_company, name must make a unique set.") {
                errorDict["name"] = "A client with this name already exists."
            }
            else if (fieldName === "contacts") {
                errorDict["contacts"] = "Please correct the contact errors below:"

                // Apply the nested errors
                this.setState((state, props) => {
                    let updatedState = state
                    message.map((contactError, index) => updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]].contacts[index].errors = contactError)
                    return updatedState
                })
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud service location

    createServiceLocation = async () => {
        const endpoint = DjangoUrls["clients:api-clients-service-locations-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id)
        const endpointMethod = "POST"

        const onSuccess = (serviceLocation) => {
            this.updateServiceLocationSelection(serviceLocation)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "detailed",
                title: "Service Location created",
                supportText: renderServiceLocationString(serviceLocation)
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Service Location could not be created"
            })
        }

        this.createUpdateServiceLocation(endpoint, endpointMethod, onSuccess, onError)
    }

    updateServiceLocation = async () => {
        const endpoint = DjangoUrls["clients:api-clients-service-locations-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id, this.state.selectedServiceLocation.id)
        const endpointMethod = "PUT"

        const onSuccess = (serviceLocation) => {
            this.updateServiceLocationSelection(serviceLocation)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "detailed",
                title: "Service Location updated",
                supportText: renderServiceLocationString(serviceLocation)
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Service Location could not be updated"
            })
        }

        this.createUpdateServiceLocation(endpoint, endpointMethod, onSuccess, onError)
    }

    createUpdateServiceLocation = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "serviceLocationData"
        const submittingName = "submittingServiceLocation"
        const errorDictName = "serviceLocation"

        const dataManipulator = (data, state) => {
            data.default_invoice_net = data.default_invoice_net !== "" ? data.default_invoice_net : null  // Convert blank net value to null
            data.contacts = data.contacts || []
            return data
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "A service location with this name and address already exists.") {
                errorDict["physical_address_street"] = "A service location with this name and address already exists."
            }
            else if (fieldName === "contacts") {
                errorDict["contacts"] = "Please correct the contact errors below:"

                // Apply the nested errors
                this.setState((state, props) => {
                    let updatedState = state
                    message.map((contactError, index) => updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]].contacts[index].errors = contactError)
                    return updatedState
                })
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud PriceBook Item

    createPriceBookService = async (isDraft) => {
        const endpoint = DjangoUrls["pricebook:api-pricebookitem-list"](window.MARKETPLACE_ENTITY_SLUG)

        const onSuccess = (priceBookItem) => {
            this.updatePriceBookServiceSelection(priceBookItem)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Service "${priceBookItem.description}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Service could not be created"
            })
        }

        this.CUDPriceBookItem(endpoint, "POST", onSuccess, onError)
    }

    updatePriceBookService = async () => {
        const endpoint = DjangoUrls["pricebook:api-pricebookitem-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedPriceBookService.id)

        const onSuccess = (priceBookItem) => {
            this.updatePriceBookServiceSelection(priceBookItem)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Service "${priceBookItem.description}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Service could not be updated"
            })
        }

        this.CUDPriceBookItem(endpoint, "PUT", onSuccess, onError)
    }

    createPriceBookTax = async (isDraft) => {
        const endpoint = DjangoUrls["pricebook:api-pricebookitem-list"](window.MARKETPLACE_ENTITY_SLUG)

        const returnMode = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].returnMode

        const onSuccess = (priceBookItem) => {
            const switchToForm = () => {
                this.switchToPrimaryForm()

                if (CLIENT_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state.clientData), null)
                }
                else if (SERVICE_LOCATION_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state.serviceLocationData), null)
                }
            }
            this.updatePriceBookTaxSelection(priceBookItem, switchToForm)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Tax Rate "${priceBookItem.description}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Tax Rate could not be created"
            })
        }

        this.CUDPriceBookItem(endpoint, "POST", onSuccess, onError)
    }

    updatePriceBookTax = async () => {
        const priceBookItemID = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].id
        const endpoint = DjangoUrls["pricebook:api-pricebookitem-detail"](window.MARKETPLACE_ENTITY_SLUG, priceBookItemID)

        const returnMode = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].returnMode

        const onSuccess = (priceBookItem) => {
            const switchToForm = () => {
                this.switchToPrimaryForm()

                if (CLIENT_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state.clientData), null)
                }
                else if (SERVICE_LOCATION_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state.serviceLocationData), null)
                }
            }
            this.updatePriceBookTaxSelection(priceBookItem, switchToForm)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Tax Rate "${priceBookItem.description}" updated`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Tax Rate could not be updated"
            })
        }

        this.CUDPriceBookItem(endpoint, "PUT", onSuccess, onError)
    }

    CUDPriceBookItem = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "priceBookItemData"
        const submittingName = "submittingPriceBookItem"
        const errorDictName = "priceBookItem"

        const dataManipulator = (data, state) => {
            let finalData = deepcopy()(data)
            finalData.confirmed = true

            // If this isn't a service charge, remove service-charge-specific data
            if (state.priceBookItemData.pricebook_item_type !== PriceBookItemTypes.service) {
                delete finalData.default_unit_type
                delete finalData.expected_job_duration
            }

            return finalData
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields description, service_company must make a unique set.") {
                errorDict["description"] = "A PriceBook item with this name already exists."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud equipment

    createEquipment = async () => {
        const endpoint = DjangoUrls["equipment:api-service-location-equipment-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedServiceLocation.id)
        const endpointMethod = "POST"

        const onSuccess = (equipment) => {
            this.updateEquipmentSelection([...this.state.selectedEquipment, equipment])
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Equipment "${equipment.display_name}" created`,
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Equipment could not be created",
            })
        }

        this.createUpdateEquipment(endpoint, endpointMethod, onSuccess, onError)
    }

    createUpdateEquipment = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "equipmentData"
        const submittingName = "submittingEquipment"
        const errorDictName = "equipment"

        const dataManipulator = (data, state) => {
            data.equipment_category = data.equipment_category || null
            data.equipment_type = data.equipment_type || null
            data.warranties = data.warranties || []

            if (data.ownership_type === "") {
                delete data.ownership_type
            }

            return data
        }

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "The fields service_location, display_name must make a unique set.") {
                errorDict["display_name"] = "A piece of equipment with this name at this service location already exists."
            }
            else if (fieldName === "non_field_errors" && message === "There are other pieces of equipment that belong to this client with the same manufacturer, model number, and serial number.") {
                errorDict["identifying_info"] = "Please ensure this equipment's manufacturer, model number, and serial number are unique for this Client."
            }
            else if (fieldName === "warranties") {
                errorDict["warranties"] = "Please correct the warranty errors below:"

                // Apply the nested errors
                this.setState((state, props) => {
                    let updatedState = state
                    message.map((warrantyError, index) => updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]].warranties[index].errors = warrantyError)
                    return updatedState
                })
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, setErrors)
    }

    // Crud Contact

    createContact = async () => {
        const attachedTo = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].attached_to
        const populationRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].populationRef

        const contactData = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]]

        let endpoint

        if (attachedTo === "external_client") {
            endpoint = DjangoUrls["clients:api-clients-contacts-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id)
        }
        else {
            endpoint = DjangoUrls["clients:api-clients-service-locations-contacts-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id, this.state.selectedServiceLocation.id)
        }

        const endpointMethod = "POST"

        const onSuccess = (contact) => {
            this[populationRef](contact)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: contact.is_ephemeral ? "detailed" : "default",
                title: `Contact ${contact.is_ephemeral ? "added" : "created"}`,
                supportText: `This contact will not be saved to the contact list`
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: `Contact could not be ${contactData.is_ephemeral ? "added" : "created"}`,
            })
        }

        this.createUpdateContact(endpoint, endpointMethod, onSuccess, onError)
    }

    updateContact = async () => {
        const attachedTo = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].attached_to
        const populationRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].populationRef
        const dataRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].dataRef
        let endpoint

        if (attachedTo === "external_client") {
            endpoint = DjangoUrls["clients:api-clients-contacts-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id, this.state[dataRef].id)
        }
        else {
            endpoint = DjangoUrls["clients:api-clients-service-locations-contacts-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.selectedClient.id, this.state.selectedServiceLocation.id, this.state[dataRef].id)
        }

        const endpointMethod = "PUT"
        const onSuccess = (contact) => {
            this[populationRef](contact)
            this.switchToPrimaryForm()
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "Contact updated",
            })
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Contact could not be updated",
            })
        }

        this.createUpdateContact(endpoint, endpointMethod, onSuccess, onError)
    }

    createUpdateContact = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "contactData"
        const submittingName = "submittingContact"
        const errorDictName = "contact"

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "A contact with these details already exists.") {
                errorDict["non_field_error"] = "A contact with these details already exists."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, undefined, setErrors)
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "JOB_PREVIEW":
                this.createDraftJob(true)
                break
            case "JOB_CREATE":
                this.createJob()
                break
            case "JOB_CREATE_DRAFT":
                this.createDraftJob()
                break
            case "JOB_DELETE_DRAFT":
                window.deleteDraftJob = this.deleteDraftJob
                document.querySelector("#message_modal_confirm_delete_draft").style.display = ""
                window.MicroModal.show("message_modal_confirm_delete_draft")
                break
            case "JOB_UPDATE":
                this.updateJob()
                break
            case "JOB_CANCEL_EDITS":
                location.assign(DjangoUrls["jobs:jobs-detail"](window.MARKETPLACE_ENTITY_SLUG, window.JOB_ID))
                break
            case "CLIENT_CREATE":
                this.createClient()
                break
            case "CLIENT_UPDATE":
                this.updateClient()
                break
            case "CLIENT_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "CLIENT_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "BILLING_CLIENT_CREATE":
                this.createBillingClient()
                break
            case "BILLING_CLIENT_UPDATE":
                this.updateBillingClient()
                break
            case "BILLING_CLIENT_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "BILLING_CLIENT_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "SERVICE_LOCATION_CREATE":
                this.createServiceLocation()
                break
            case "SERVICE_LOCATION_UPDATE":
                this.updateServiceLocation()
                break
            case "SERVICE_LOCATION_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "SERVICE_LOCATION_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            case "PRICEBOOK_SERVICE_CREATE":
                this.createPriceBookService()
                break
            case "PRICEBOOK_SERVICE_UPDATE":
                this.updatePriceBookService()
                break
            case "PRICEBOOK_TAX_CREATE":
                this.createPriceBookTax()
                break
            case "PRICEBOOK_TAX_UPDATE":
                this.updatePriceBookTax()
                break
            case "PRICEBOOK_TAX_CANCEL_CREATE":
                const returnMode = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].returnMode

                this.switchToPrimaryForm()

                if (CLIENT_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state[FORM_DATA_NAMES_BY_MODE[returnMode]]), null)
                }
                else if (SERVICE_LOCATION_FORM_MODES.includes(returnMode)) {
                    this.switchToSecondaryForm(returnMode, deepcopy()(this.state.serviceLocationData), null)
                }
                break
            case "EQUIPMENT_CREATE":
                this.createEquipment()
                break
            case "CONTACT_CREATE":
                this.createContact()
                break
            case "CONTACT_UPDATE":
                const dataRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].dataRef

                if (this.state[dataRef].is_ephemeral) {
                    this.createContact()
                }
                else {
                    this.updateContact()
                }
                break
            case "CONTACT_CANCEL_CREATE":
                this.switchToPrimaryForm()
                break
            case "CONTACT_CANCEL_EDITS":
                this.switchToPrimaryForm()
                break
            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render() {
        if (this.state.jobData === null || this.state.estimateData === null || this.state.recallToData === null) {
            return <Spinner centered={true} />
        }
        else {
            if (PRIMARY_FORM_MODES.includes(this.state.mode)) {
                return <JobForm
                    mode={this.state.mode}
                    submitting={this.state.submittingJob}
                    job={this.state.jobData}
                    estimate={this.state.estimateData}
                    recallTo={this.state.recallToData}
                    errors={this.state.errors.job}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("jobData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    updateClientSelection={this.updateClientSelection}
                    updateBillingClientSelection={this.updateBillingClientSelection}
                    updateServiceLocationSelection={this.updateServiceLocationSelection}
                    updatePriceBookServiceSelection={this.updatePriceBookServiceSelection}
                    updateEquipmentSelection={this.updateEquipmentSelection}
                    updateReporterSelection={this.updateReporterSelection}
                    updatePointOfContactSelection={this.updatePointOfContactSelection}
                    workingTechnicianOptions={this.workingTechnicianOptions}
                    selectedClient={this.state.selectedClient}
                    selectedBillingClient={this.state.selectedBillingClient}
                    selectedServiceLocation={this.state.selectedServiceLocation}
                    selectedPriceBookService={this.state.selectedPriceBookService}
                    selectedEquipment={this.state.selectedEquipment}
                    selectedReporter={this.state.selectedReporter}
                    selectedPointOfContact={this.state.selectedPointOfContact}
                    showEquipmentWipedWarning={this.state.showEquipmentWipedWarning}
                    showContactsWipedWarning={this.state.showContactsWipedWarning}
                    similarJobs={this.state.similarJobData}
                    outstandingBalance={this.state.outstandingBalance}
                    jobOriginTypeOptions={this.state.jobOriginTypeOptions}
                    allJobOriginTypes={this.state.allJobOriginTypes}
                    priorityLevelsOptions={this.state.priorityLevelsOptions}
                    preferredTimezone={this.state.preferredTimezone}
                    showJobOriginFields={this.state.showJobOriginFields}
                    showCustomJobIDField={this.state.showCustomJobIDField}
                    defaultCountryCode={this.state.phoneNumberCountry}
                    formatCurrencyValue={currencyFormatter(this.state.currencyCode, this.state.languageCode)}
                    updateAttachments={this.updateAttachments}
                    removePendingAttachment={this.removePendingAttachment}
                    isUploading={this.state.isUploading}
                    setIsUploading={this.setIsUploading}
                    pendingSessionId={this.state.pendingSessionId}
                    returnScroll={this.state.returnScroll}
                ></JobForm>
            }
            else if (CLIENT_FORM_MODES.includes(this.state.mode)) {
                return <ClientForm
                    mode={this.state.mode}
                    submitting={this.state[SUBMITTING_NAMES_BY_MODE[this.state.mode]]}
                    client={this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]]}
                    errors={this.state.errors[ERROR_NAMES_BY_MODE[this.state.mode]]}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData(FORM_DATA_NAMES_BY_MODE[this.state.mode], fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    showServiceLocationSelect={false}
                    defaultCountryCode={this.state.phoneNumberCountry}
                    currencySymbol={getCurrencySymbol(this.state.currencyCode, this.state.languageCode)}
                    defaultClientType={this.state.defaultClientType}
                    useTaxes={this.state.useTaxes}
                    priceBookTaxes={this.state.priceBookTaxes}
                    showTaxCreateButton={this.state.showTaxCreateButton}
                    selectedDefaultBillingClient={this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].selectedDefaultBillingClient}
                    updateDefaultBillingClientSelection={this.updateDefaultBillingClientSelection}
                    selectedPriceBookTax={(
                        this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].selectedPriceBookTax ||
                        (this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].default_pricebook_tax && getPricebookTaxById(this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].default_pricebook_tax, this.state.priceBookTaxes))
                    )}
                    updatePriceBookTaxSelection={this.updatePriceBookTaxSelection}
                    returnScroll={0}
                />
            }
            else if (SERVICE_LOCATION_FORM_MODES.includes(this.state.mode)) {
                return <ServiceLocationForm
                    mode={this.state.mode}
                    submitting={this.state.submittingServiceLocation}
                    client={this.state.selectedClient}
                    serviceLocation={this.state.serviceLocationData}
                    errors={this.state.errors.serviceLocation}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("serviceLocationData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    defaultCountryCode={this.state.phoneNumberCountry}
                    useTaxes={this.state.useTaxes}
                    showTaxCreateButton={this.state.showTaxCreateButton}
                    selectedDefaultBillingClient={this.state.selectedClient?.default_billing_client}
                    selectedPriceBookTax={(
                        this.state.serviceLocationData.selectedPriceBookTax ||
                        (this.state.serviceLocationData.default_pricebook_tax && getPricebookTaxById(this.state.serviceLocationData.default_pricebook_tax, this.state.priceBookTaxes))
                    )}
                    updatePriceBookTaxSelection={this.updatePriceBookTaxSelection}
                    returnScroll={0}
                />
            }
            else if (PRICEBOOK_SERVICE_FORM_MODES.includes(this.state.mode)) {
                return <PriceBookItemForm
                    mode={this.state.mode}
                    submitting={this.state.submittingPriceBookItem}
                    priceBookItem={this.state.priceBookItemData}
                    errors={this.state.errors.priceBookItem}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("priceBookItemData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    currencySymbol={getCurrencySymbol(this.state.currencyCode, this.state.languageCode)}
                    showQuickBooksRevenueAccountSelect={this.state.showQuickBooksRevenueAccountSelect}
                    showQuickBooksTaxAgencyVendorSelect={this.state.showQuickBooksTaxAgencyVendorSelect}
                    useTaxes={this.state.useTaxes}
                    pricebookDefaultTaxableService={this.state.pricebook_default_taxable_service}
                    pricebookDefaultTaxablePart={this.state.pricebook_default_taxable_part}
                    pricebookDefaultTaxableOther={this.state.pricebook_default_taxable_other}
                    returnScroll={0}
                ></PriceBookItemForm>
            }
            else if (EQUIPMENT_FORM_MODES.includes(this.state.mode)) {
                return <EquipmentForm
                    mode={this.state.mode}
                    submitting={this.state.submittingEquipment}
                    equipment={this.state.equipmentData}
                    errors={this.state.errors.equipment}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("equipmentData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    equipmentCategoryOptions={this.state.equipmentCategoryOptions}
                    equipmentTypeOptions={this.state.equipmentTypeOptions}
                    ownershipTypeOptions={this.state.ownershipTypeOptions}
                    duplicateEquipment={this.state.duplicateEquipmentData}
                    returnScroll={0}
                ></EquipmentForm>
            }
            else if (CONTACT_FORM_MODES.includes(this.state.mode)) {
                return <ContactForm
                    mode={this.state.mode}
                    submitting={this.state.submittingContact}
                    contact={this.state.contactData}
                    errors={this.state.errors.contact}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("contactData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    returnScroll={0}
                ></ContactForm>
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
    }
}

export default JobCreateContainer;
